import type { LocalizedBuildingType } from '@arcexperts/core-types'

export type BuildingTypeDirectoryEntry = Omit<LocalizedBuildingType, 'usageType'> & { usageType: string }

type UsageBuildingTypeSelection = {
  type?: string | null
  typeCounter?: number
  buildingTypeId?: string | null
  buildingTypeName?: string | null
}

const LEGACY_COMPATIBLE_USAGE_TYPES: Record<string, string> = {
  HOUSING: 'LEISURE',
  PRODUCTION: 'PRODUCTION_AND_STORAGE',
  STORAGE: 'PRODUCTION_AND_STORAGE',
}

function usageTypesAreCompatible(selectedUsageType: string | null | undefined, directoryUsageType: string): boolean {
  return (
    selectedUsageType === directoryUsageType ||
    LEGACY_COMPATIBLE_USAGE_TYPES[selectedUsageType ?? ''] === directoryUsageType
  )
}

export function migrateStandardUsageCategories<T extends UsageBuildingTypeSelection>(
  usageItems: T[] | undefined,
  buildingTypes: BuildingTypeDirectoryEntry[]
): { usageItems: T[]; changed: boolean } {
  if (!Array.isArray(usageItems) || usageItems.length === 0) {
    return { usageItems: usageItems ?? [], changed: false }
  }

  const buildingTypesById = new Map(buildingTypes.map((buildingType) => [buildingType.id, buildingType]))
  const buildingTypesByName = new Map<string, BuildingTypeDirectoryEntry[]>()
  for (const buildingType of buildingTypes) {
    buildingTypesByName.set(buildingType.name, [...(buildingTypesByName.get(buildingType.name) ?? []), buildingType])
  }
  const typeCounts = new Map<string, number>()
  let changed = false

  const migratedUsageItems = usageItems.map((usageItem) => {
    const nameMatches = usageItem.buildingTypeName ? (buildingTypesByName.get(usageItem.buildingTypeName) ?? []) : []
    const buildingType = usageItem.buildingTypeId
      ? buildingTypesById.get(usageItem.buildingTypeId)
      : nameMatches.length === 1
        ? nameMatches[0]
        : undefined
    const nextType =
      usageItem.type === 'PRODUCTION' || usageItem.type === 'STORAGE'
        ? 'PRODUCTION_AND_STORAGE'
        : usageItem.type === 'HOUSING' && buildingType?.usageType === 'LEISURE'
          ? 'LEISURE'
          : usageItem.type
    const nextTypeCounter = typeCounts.get(nextType ?? '') ?? 0
    typeCounts.set(nextType ?? '', nextTypeCounter + 1)

    if (nextType === usageItem.type && usageItem.typeCounter === nextTypeCounter) {
      return usageItem
    }

    changed = true
    return {
      ...usageItem,
      type: nextType,
      typeCounter: nextTypeCounter,
    }
  })

  return {
    usageItems: changed ? migratedUsageItems : usageItems,
    changed,
  }
}

export function normalizeUsageBuildingTypeSelections<T extends UsageBuildingTypeSelection>(
  usageItems: T[] | undefined,
  _country: unknown,
  buildingTypes: BuildingTypeDirectoryEntry[]
): { usageItems: T[]; changed: boolean } {
  if (!Array.isArray(usageItems) || usageItems.length === 0) {
    return { usageItems: usageItems ?? [], changed: false }
  }

  const hasDirectory = buildingTypes.length > 0
  const buildingTypesById = new Map(buildingTypes.map((buildingType) => [buildingType.id, buildingType]))
  const buildingTypesByName = new Map<string, BuildingTypeDirectoryEntry[]>()
  for (const buildingType of buildingTypes) {
    buildingTypesByName.set(buildingType.name, [...(buildingTypesByName.get(buildingType.name) ?? []), buildingType])
  }
  let changed = false

  const normalizedUsageItems = usageItems.map((usageItem) => {
    const normalizedBuildingTypeId =
      typeof usageItem.buildingTypeId === 'string' && usageItem.buildingTypeId.trim()
        ? usageItem.buildingTypeId.trim()
        : null
    const normalizedBuildingTypeName =
      typeof usageItem.buildingTypeName === 'string' && usageItem.buildingTypeName.trim()
        ? usageItem.buildingTypeName.trim()
        : null

    if (!normalizedBuildingTypeId) {
      if (!normalizedBuildingTypeName) {
        return usageItem
      }

      if (!hasDirectory) {
        return usageItem
      }

      const nameMatches = buildingTypesByName.get(normalizedBuildingTypeName) ?? []
      const matchedBuildingType =
        nameMatches.length === 1 && usageTypesAreCompatible(usageItem.type, nameMatches[0].usageType)
          ? nameMatches[0]
          : null

      if (matchedBuildingType) {
        changed = true
        return {
          ...usageItem,
          buildingTypeId: matchedBuildingType.id,
          buildingTypeName: matchedBuildingType.name,
        }
      }

      changed = true
      return {
        ...usageItem,
        buildingTypeId: null,
        buildingTypeName: null,
      }
    }

    if (!hasDirectory) {
      return usageItem
    }

    const matchedBuildingType = buildingTypesById.get(normalizedBuildingTypeId)
    const isInvalidSelection =
      !matchedBuildingType || !usageTypesAreCompatible(usageItem.type, matchedBuildingType.usageType)

    if (!isInvalidSelection) {
      if (normalizedBuildingTypeName === matchedBuildingType.name) {
        return usageItem
      }

      changed = true
      return {
        ...usageItem,
        buildingTypeId: normalizedBuildingTypeId,
        buildingTypeName: matchedBuildingType.name,
      }
    }

    changed = true
    return {
      ...usageItem,
      buildingTypeId: null,
      buildingTypeName: null,
    }
  })

  return {
    usageItems: changed ? normalizedUsageItems : usageItems,
    changed,
  }
}
